import React, {useContext} from 'react';

import {Theme} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Grid from '@mui/material/Grid';
import Button from 'components/Button';
import RecursiveTreeView from 'components/RecursiveTreeView';
import Panel from 'components/Panel';
import {IDiplomatFolder} from 'types/DiplomatFolder';
import {IDiplomatObject} from 'types/DiplomatObject';
import {find, intersectionWith, sortBy} from 'lodash';
import {unpackId} from 'common/diplomatIdUtil';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import classNames from 'classnames';

const useStyles = makeStyles((theme: Theme) =>
   createStyles({
      root: {
         margin: 'auto',
         marginBottom: theme.spacing(4),
      },
      paper: {
         width: 200,
         height: 230,
         overflow: 'auto',
         borderColor: theme.palette.primary.main,
      },
      button: {
         margin: theme.spacing(0.5, 0),
      },
      list: {
         height: '30rem',
         overflow: 'auto',
      },
      disabled: {
         position: 'relative',
         '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,.1)', // theme.palette.grey[100],
            zIndex: 10,
         },
      },
   })
);

type Option = IDiplomatObject;
function not(a: Option[], b: Option[]) {
   return a.filter((value) => !find(b, (o) => o.id === value.id));
}

type FormikTransferTreeProps = {
   name: string;
   leftLabel: string;
   rightLabel: string;
   options: IDiplomatObject[];
   folders: IDiplomatFolder[];
};

const FormikTransferTree = ({
   name,
   leftLabel,
   rightLabel,
   options,
   folders,
}: FormikTransferTreeProps) => {
   const classes = useStyles();
   const {field} = useController({name, defaultValue: []});
   const {setValue} = useFormContext();
   const disabled = useContext(readOnlyContext);

   const [left, setLeft] = React.useState<IDiplomatObject[]>(() => not(options, field.value));
   React.useEffect(() => {
      // console.debug('setLeft effect', options, field.value, not(options, field.value));
      setLeft(not(options, field.value));
   }, [options, field.value, setLeft]);

   const [selected, select] = React.useState<string[]>([]);
   const leftSelected = React.useMemo(
      () => intersectionWith(left, selected, (b, a) => b.id === a),
      [selected, left]
   );
   const rightSelected = React.useMemo(
      () => intersectionWith(field.value, selected, (b: IDiplomatObject, a) => b.id === a),
      [selected, field.value]
   );

   const handleSelect = React.useCallback(
      (ev: any, packedIds: string[]) => {
         // console.debug('handleSelect', ev, packedIds);
         const ids = packedIds.map((pId) => unpackId(pId).id);
         select(ids);
      },
      [select]
   );

   const moveRight = () => {
      setValue(name, sortBy([...field.value, ...leftSelected], 'name'), SetValueOptions);
      // console.debug('moveRight', left, leftSelected, not(left, leftSelected));
      setLeft(not(left, leftSelected));
      // select(not(checked, leftChecked));
   };

   const moveLeft = () => {
      setValue(name, not(field.value, rightSelected), SetValueOptions);
      setLeft(sortBy([...left, ...field.value], 'name'));
      // select(not(checked, rightChecked));
   };

   return (
      <>
         <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            className={classes.root}
         >
            <Grid item xs={5}>
               <Panel name={leftLabel} className={classNames({[classes.disabled]: disabled})}>
                  <RecursiveTreeView
                     className={classes.list}
                     folders={folders}
                     objects={left}
                     onNodeSelect={handleSelect}
                     // disabled={disabled}
                  />
               </Panel>
            </Grid>
            <Grid item xs={1}>
               <Grid container direction="column" alignItems="center">
                  <Button
                     variant="outlined"
                     size="small"
                     className={classes.button}
                     onClick={moveLeft}
                     disabled={disabled || rightSelected.length === 0}
                     aria-label="move selected left"
                  >
                     &lt;
                  </Button>
                  <Button
                     variant="outlined"
                     size="small"
                     className={classes.button}
                     onClick={moveRight}
                     disabled={disabled || leftSelected.length === 0}
                     aria-label="move selected right"
                  >
                     &gt;
                  </Button>
               </Grid>
            </Grid>
            <Grid item xs={5}>
               <Panel name={rightLabel} className={classNames({[classes.disabled]: disabled})}>
                  <RecursiveTreeView
                     className={classes.list}
                     folders={folders}
                     objects={field.value}
                     onNodeSelect={handleSelect}
                     // disabled={disabled}
                  />
               </Panel>
            </Grid>
         </Grid>
      </>
   );
};

export default React.memo(FormikTransferTree);
